import * as React from 'react';
import { fieldTypeToFilterType } from '../../atoms/additionalFieldColumns';
import { Field } from '../../atoms/forms';
import { FilterId } from '../../components_v2/filter/model/Model';
import { Column, DataType } from '../../components_v2/table/Table';
import { TableRow } from '../orders/templateOrders/style/Style';
import { Promotion, PromotionProduct } from './model';
import { translateToString } from '../../styles/global/translate';
import { DarkGreySidely, FilterWhite, PastelPrimaryColor } from '../../styles/global/css/Utils';
import Switch from '../../components_v2/Switch/Switch';
import * as moment from 'moment';
import { RangePickerState } from './Promotions';
import { useWindowDimensions } from '../../components_v2/utils';
import styled from 'styled-components';
import arrow_right_date_picker from 'images/icon/arrow_right_date_picker.svg';
import * as ReactDates from 'react-dates';
import { PutPromotionBody } from './actions';
import { DateAccessor, EditableContent } from '../client-companies/data/CompanyColumns';
import { ModalState } from '../products/model';
import { TableCellButton } from '../client-companies/style/Style';
import { LoadingStateEnum } from '../import/model';
import { PopupMode } from '../../components_v2/popup/model/Model';
import { PromotionFieldCell } from './PopupPromotion';
import { isComputedField, promotionFieldDisplay, promotionFieldRange } from './fieldDisplay';
import { CalculatedValueCell, calculatedSortValue } from './calculatedValue';
import { CalculatedField } from '../../atoms/calculatedFields';
import { Eye } from 'lucide-react';

const RangeControllerInputDiv = styled.div`
	display: flex;
	align-items: center;
`;

const TimeButton = styled.div<{ noEdit?: boolean }>`
	border-radius: 5px;
	padding: 11px 11px 9px;
	font-size: 13px;
	font-weight: 500;
	width: 100px;
	text-align: center;
	${p => p.noEdit ? '' : 'cursor: pointer;'}
	${p => p.noEdit ? '' : `
		&:hover {
			background-color: ${PastelPrimaryColor};
		}
	`}
`;

export function RangeControllerInput(props: {
	startDate: moment.Moment | null,
	endDate: moment.Moment | null,
	setRangePickerState?: React.Dispatch<React.SetStateAction<RangePickerState>>,
	onDatesChange?: (p: { startDate: moment.Moment | null, endDate: moment.Moment | null }) => void,
	noEdit?: boolean,
	white?: boolean
}) {
	const { height } = useWindowDimensions();
	const { startDate, endDate, onDatesChange } = props;

	const onClick = (focus: ReactDates.FocusedInputShape) => (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		const bounds = e.currentTarget.getBoundingClientRect();
		const isTop = e.screenY > height / 2;
		props.setRangePickerState?.({
			isOpen: true,
			isTop,
			top: !isTop ? bounds.top + bounds.height + 20 : bounds.top - bounds.height,
			left: bounds.left - 265 - bounds.width / 2,
			dates: { startDate, endDate },
			onDatesChange,
			focus
		});
	};

	return <RangeControllerInputDiv>
		<TimeButton onClick={onClick('startDate')} noEdit={props.noEdit}>
			{startDate?.format('L') ?? '-'}
		</TimeButton>
		<img src={arrow_right_date_picker} style={{ filter: props.white ? FilterWhite : undefined }} />
		<TimeButton onClick={onClick('endDate')} noEdit={props.noEdit}>
			{endDate?.format('L') ?? '-'}
		</TimeButton>
	</RangeControllerInputDiv>;
}

// One table line: a promotion, or a (promotion, linked product) pair when the
// one-line-per-product toggle is on — the promotion accessors keep working
export type PromotionRow = Promotion & { product?: PromotionProduct }

// the display rules of a field's value, shared with the popup's grids
export { promotionFieldDisplay } from './fieldDisplay';

/// The value a column sorts on (client-side: the whole listing is loaded)
export function promotionRowSortValue(row: PromotionRow, columnId: string, fieldsById: Map<number, Field>): string | number | undefined {
	if (columnId === 'name') return row.name?.toLowerCase();
	if (columnId === 'created_at') return Date.parse(row.created_at);
	if (columnId === 'companies') return row.companies.length;
	if (columnId === 'is_active') return row.is_active ? 1 : 0;
	if (columnId === 'product_name') return row.product?.name?.toLowerCase();
	if (columnId.startsWith('name_')) return row.periods[Number(columnId.slice(5))]?.name?.toLowerCase();
	if (columnId.startsWith('calc_')) return calculatedSortValue(row.calculated?.[columnId.slice(5)]);
	if (columnId.startsWith('link_calc_')) return calculatedSortValue(row.product?.calculated?.[columnId.slice(10)]);
	if (columnId.startsWith('period_')) {
		const start = row.periods[Number(columnId.slice(7))]?.start;
		return start ? Date.parse(start) : undefined;
	}
	const link = columnId.startsWith('link_field_');
	const field = fieldsById.get(Number(link ? columnId.slice(11) : columnId.startsWith('field_') ? columnId.slice(6) : NaN));
	if (!field) return undefined;
	const raw = link ? row.product?.fields?.[field.id.toString()] : row.fields?.[field.id.toString()];
	if (raw === null || raw === undefined) return undefined;
	switch (field.type) {
		case 'Money':
		case 'Number':
			return Number(raw);
		case 'Date':
			return Date.parse(String(raw));
		case 'Boolean':
			return raw ? 1 : 0;
		default:
			return promotionFieldDisplay(field, raw).toLowerCase();
	}
}

export function comparePromotionRows(a: PromotionRow, b: PromotionRow, columnId: string, fieldsById: Map<number, Field>): number {
	const va = promotionRowSortValue(a, columnId, fieldsById);
	const vb = promotionRowSortValue(b, columnId, fieldsById);
	if (va === undefined) return vb === undefined ? 0 : 1;
	if (vb === undefined) return -1;
	if (typeof va === 'number' && typeof vb === 'number') return va - vb;
	return String(va).localeCompare(String(vb));
}

export default function genPromotionColumns(props: {
	promotions: Promotion[],
	setRangePickerState: React.Dispatch<React.SetStateAction<RangePickerState>>,
	putPromotion: (id: number, body: PutPromotionBody) => Promise<void>,
	// saves one link-level field value on the line's (promotion, product) pair
	putProductField: (promotionId: number, productId: number, fieldId: number, value: unknown) => Promise<void>,
	setModalState: React.Dispatch<React.SetStateAction<ModalState<{ promotion: Promotion, mode: PopupMode }>>>,
	loadingState: LoadingStateEnum | undefined,
	// the campaign update right: without it the inline cells (period names,
	// period dates, visibility) render read-only like the API enforces
	canUpdate: boolean,
	// the promotion-level custom fields, one displayed column each
	promotionFields: Field[],
	// the link-level (promotion x product) fields, shown when inlining
	linkFields: Field[],
	// the calculated fields of the promotions and of their products
	calculatedFields?: CalculatedField[],
	productCalculatedFields?: CalculatedField[],
	// one line per linked product: adds the product and link-field columns
	inlineProducts: boolean,
}): Array<Column<PromotionRow>> {
	const { promotions, setRangePickerState, putPromotion, putProductField, setModalState, loadingState, canUpdate, promotionFields, linkFields, inlineProducts } = props;
	const calculatedFields = props.calculatedFields ?? [];
	const productCalculatedFields = props.productCalculatedFields ?? [];
	const maxPeriodsLength = promotions.reduce((acc, p) => Math.max(acc, p.periods.length), 1);
	const cols: Array<Column<PromotionRow>> = [
		{
			id: 'name',
			Header: translateToString('name'),
			accessor: row => <TableRow
				fontWeight="500"
				color={DarkGreySidely}
				cursor="pointer"
				onClick={() => setModalState({ isOpen: true, data: { promotion: row, mode: PopupMode.Default } })}
			>
				{row.name}
			</TableRow>,
			hoverImage: row => <TableCellButton onClick={() => setModalState({ isOpen: true, data: { promotion: row, mode: PopupMode.Details } })} icon={Eye} />,
			width: 200,
			type: 'string',
			freeze: 'left',
			toolTip: row => row.name,
			// header filter buttons, like the companies listing: each column
			// carrying a matching entry of the filter panel opens it directly
			filterId: 'name',
		},
		{
			id: 'created_at',
			Header: translateToString('created_at'),
			accessor: row => <DateAccessor date={row.created_at} />,
			width: 150,
			disableFilter: true,
		},
	];
	if (inlineProducts) {
		cols.splice(1, 0, {
			id: 'product_name',
			Header: translateToString('report_editor.crm_fields.Product'),
			accessor: row => <TableRow title={row.product?.name}>{row.product?.name ?? '-'}</TableRow>,
			width: 200,
			type: 'string',
			disableFilter: true,
		});
	}
	for (let index = 0; index < maxPeriodsLength; index++) {
		cols.push({
			id: `name_${index}`,
			Header: translateToString('name_period') + ' ' + (index + 1),
			accessor: row => {
				if (!row.periods[index]) return <TableRow />;
				if (!canUpdate) return <TableRow>{row.periods[index]?.name}</TableRow>;
				return <EditableContent<DataType<Promotion>>
					innerId={`${row.id}-${row.product?.id ?? ''}-${loadingState}`}
					row={row}
					defaultValue={row.periods[index]?.name}
					valueKey="period_name"
					onValueUpdate={value => putPromotion(row.id, {
						periods: row.periods.map((p, i) => {
							if (i !== index)
								return p;
							return {
								...p,
								name: value ?? p.name
							};
						})
					})} />;
			},
			width: 150,
			editable: canUpdate,
			disableFilter: true,
		});
		cols.push({
			id: `period_${index}`,
			Header: translateToString('period') + ' ' + (index + 1),
			accessor: row => {
				if (!row.periods[index]) return <TableRow />;
				const startDate = row.periods[index]?.start ? moment(row.periods[index]?.start) : null;
				const endDate = row.periods[index]?.end ? moment(row.periods[index]?.end) : null;
				if (!canUpdate) return <RangeControllerInput startDate={startDate} endDate={endDate} noEdit />;
				return <RangeControllerInput startDate={startDate} endDate={endDate} setRangePickerState={setRangePickerState} onDatesChange={dates => {
					putPromotion(row.id, {
						periods: row.periods.map((p, i) => {
							if (i !== index) return p;
							return {
								...p,
								start: dates.startDate?.utc().toISOString() ?? p.start,
								end: dates.endDate?.utc().toISOString() ?? p.end,
							};
						})
					});
				}} />;
			},
			width: 242,
			filterId: { promotions: { type: 'period_start', field_id: index, filter_type: 'temporal' } } as FilterId,
		});
	}
	// one column per promotion custom field, like the client company additional
	// columns, editable in place (the popup grid's cell): a promotion-level
	// value is shared by every product line of the promotion, so the refresh
	// after the save updates them all together
	// the bounds of each field's values over the loaded promotions, for a
	// relative colour scale
	const ranges = new Map(promotionFields.map(f => [f.id, promotionFieldRange(f, promotions.map(p => p.fields?.[f.id.toString()]))]));
	const linkRanges = new Map(linkFields.map(f => [f.id, promotionFieldRange(f, promotions.flatMap(p => p.products.map(product => product.fields?.[f.id.toString()])))]));
	cols.push(...promotionFields.map((field): Column<PromotionRow> => ({
		id: `field_${field.id}`,
		Header: field.name,
		accessor: row => <PromotionFieldCell
			field={field}
			value={row.fields?.[field.id.toString()]}
			shown={ranges.get(field.id)}
			readonly={!canUpdate}
			onSave={value => putPromotion(row.id, { fields: { updated: { ...row.fields, [field.id.toString()]: value } } })}
		/>,
		width: 150,
		editable: canUpdate && !isComputedField(field),
		type: field.type === 'Number' || field.type === 'Money' ? 'numeric' : 'string',
		filterId: { promotions: { type: 'promotion', field_id: field.id, filter_type: fieldTypeToFilterType(field.type) } } as FilterId,
	})));
	if (inlineProducts) {
		// the link-level (promotion x product) values of the line's product,
		// editable per line: they belong to this line's pair only
		cols.push(...linkFields.map((field): Column<PromotionRow> => ({
			id: `link_field_${field.id}`,
			Header: field.name,
			accessor: row => row.product
				? <PromotionFieldCell
					field={field}
					value={row.product.fields?.[field.id.toString()]}
					shown={linkRanges.get(field.id)}
					readonly={!canUpdate}
					onSave={value => putProductField(row.id, row.product!.id, field.id, value)}
				/>
				: <TableRow>-</TableRow>,
			width: 150,
			editable: canUpdate && !isComputedField(field),
			type: field.type === 'Number' || field.type === 'Money' ? 'numeric' : 'string',
			disableFilter: true,
		})));
	}
	// the calculated fields, read-only: the promotion's own, and per product
	// on the product lines
	cols.push(...calculatedFields.map((cf): Column<PromotionRow> => ({
		id: `calc_${cf.id}`,
		Header: cf.field_name,
		accessor: row => <CalculatedValueCell field={cf} value={row.calculated?.[cf.id.toString()]} />,
		width: 150,
		type: 'numeric',
		disableFilter: true,
	})));
	if (inlineProducts) {
		cols.push(...productCalculatedFields.map((cf): Column<PromotionRow> => ({
			id: `link_calc_${cf.id}`,
			Header: cf.field_name,
			accessor: row => row.product ? <CalculatedValueCell field={cf} value={row.product.calculated?.[cf.id.toString()]} /> : <TableRow>-</TableRow>,
			width: 150,
			type: 'numeric',
			disableFilter: true,
		})));
	}
	cols.push({
		id: 'companies',
		Header: translateToString('companies'),
		accessor: row => <TableRow>{row.companies.length}</TableRow>,
		width: 120,
		type: 'numeric',
		disableFilter: true,
	});
	cols.push({
		id: 'is_active',
		Header: translateToString('visible'),
		accessor: row => <Switch value={row.is_active} readonly={!canUpdate} onChange={is_active => putPromotion(row.id, { is_active })} />,
		width: 75,
		centerRow: true,
		freeze: 'right',
		disableFilter: true,
	});
	return cols;
}
